import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Fab, Paper, Slide, Tooltip, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AutoAwesomeOutlined, CloseOutlined } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { makeApiCall } from '../../config/api';
import useAssistantChat from './useAssistantChat';
import AssistantPanel from './AssistantPanel';

// Routes where the floating assistant must not appear (public, print, forms, field crew).
// The assistant is also desktop-only and for admins, superadmins and sales: never on
// phones or tablets, and never in Crew Mode (/crew).
const HIDDEN_PREFIXES = ['/login', '/public', '/support', '/forms', '/schedule/print', '/demo', '/crew'];

const ASSISTANT_USER_TYPES = new Set(['admin', 'superadmin', 'sales']);

const ENTITY_PATTERNS = [
  { type: 'customer', regex: /^\/dashboard\/customers\/(\d+)/ },
  { type: 'job', regex: /^\/dashboard\/jobs\/(\d+)/ },
  { type: 'ticket', regex: /^\/dashboard\/tickets\/(\d+)/ },
  { type: 'task', regex: /^\/dashboard\/tasks\/(\d+)/ },
  { type: 'inspection', regex: /^\/dashboard\/inspections\/(\d+)/ },
];

const derivePageContext = (location) => {
  const path = `${location.pathname}${location.search || ''}`;
  let entity = null;
  for (const pattern of ENTITY_PATTERNS) {
    const match = location.pathname.match(pattern.regex);
    if (match) {
      entity = { type: pattern.type, id: match[1] };
      break;
    }
  }
  if (!entity && location.pathname.startsWith('/dashboard/quotes')) {
    const quoteId = new URLSearchParams(location.search).get('quoteId');
    if (quoteId) entity = { type: 'quote', id: quoteId };
  }
  return { path, title: typeof document !== 'undefined' ? document.title : undefined, entity };
};

const AssistantWidget = () => {
  const { user, isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  // Desktop = a wide screen driven by a mouse or trackpad. Phones and tablets
  // report a coarse pointer with no hover, so they never get the button.
  const isWideScreen = useMediaQuery(theme.breakpoints.up('md'));
  const hasFinePointer = useMediaQuery('(hover: hover) and (pointer: fine)');
  const isDesktop = isWideScreen && hasFinePointer;
  const canUseAssistant = ASSISTANT_USER_TYPES.has(user?.user_type);
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(null);
  const [unread, setUnread] = useState(0);

  const hidden = useMemo(
    () => HIDDEN_PREFIXES.some((prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)),
    [location.pathname],
  );

  // Ask the backend once per session whether the assistant is enabled.
  useEffect(() => {
    if (!isAuthenticated || !user || !canUseAssistant) {
      setEnabled(null);
      return;
    }
    let cancelled = false;
    makeApiCall('api/agent/status')
      .then((response) => (response.ok ? response.json() : { enabled: false }))
      .then((payload) => { if (!cancelled) setEnabled(Boolean(payload?.enabled)); })
      .catch(() => { if (!cancelled) setEnabled(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated, user, canUseAssistant]);

  const locale = (i18n.language || 'en').startsWith('es') ? 'es-MX' : 'en';
  const getPageContext = useCallback(() => derivePageContext(location), [location]);
  const onNavigate = useCallback((data) => {
    if (data?.path) navigate(data.path);
  }, [navigate]);
  const onUnread = useCallback(() => {
    setUnread((count) => (open ? 0 : count + 1));
  }, [open]);

  const chat = useAssistantChat({ locale, getPageContext, onNavigate, onUnread });

  useEffect(() => {
    if (open) setUnread(0);
  }, [open]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!isAuthenticated || !user || !canUseAssistant || !isDesktop || hidden || enabled !== true) return null;

  const panel = <AssistantPanel chat={chat} user={user} onClose={() => setOpen(false)} />;

  return (
    <>
      <Tooltip title={open ? t('assistant.close') : t('assistant.open')} placement="left">
        <Fab
          color="secondary"
          onClick={() => setOpen((value) => !value)}
          aria-label={open ? t('assistant.close') : t('assistant.open')}
          aria-expanded={open}
          aria-controls="assistant-panel"
          sx={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: theme.zIndex.modal - 50,
          }}
        >
          <Badge badgeContent={unread} color="primary" overlap="circular" invisible={unread === 0} aria-label={unread ? t('assistant.unread', { count: unread }) : undefined}>
            {open ? <CloseOutlined /> : <AutoAwesomeOutlined />}
          </Badge>
        </Fab>
      </Tooltip>

      <Slide in={open} direction="up" mountOnEnter unmountOnExit>
        <Paper
          id="assistant-panel"
          elevation={8}
          sx={{
            position: 'fixed',
            right: 24,
            bottom: 96,
            width: 'min(420px, calc(100vw - 48px))',
            height: 'min(640px, calc(100vh - 128px))',
            borderRadius: 3,
            overflow: 'hidden',
            zIndex: theme.zIndex.modal - 51,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {panel}
        </Paper>
      </Slide>
    </>
  );
};

export default AssistantWidget;
